import { Controller } from "@hotwired/stimulus"

/**
 * Dropdown Filter Controller
 * 
 * Un dropdown personalizado con checkboxes para filtros múltiples.
 * Soporta búsqueda, selección múltiple y auto-submit.
 * 
 * Uso:
 * <div data-controller="dropdown-filter" data-dropdown-filter-name-value="plan_ids[]">
 *   <button data-dropdown-filter-target="trigger">Plan ▼</button>
 *   <div data-dropdown-filter-target="menu" class="hidden">
 *     <input data-dropdown-filter-target="search" data-action="input->dropdown-filter#filter">
 *     <div data-dropdown-filter-target="options">
 *       <!-- opciones dinámicas -->
 *     </div>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ["trigger", "menu", "search", "options", "badge", "hiddenInputs"]
  static values = {
    name: String,
    options: Array,
    selected: Array,
    placeholder: { type: String, default: "Seleccionar..." }
  }

  connect() {
    // Convertir todos los valores seleccionados a strings para comparación consistente
    this.selectedValues = new Set((this.selectedValue || []).map(v => String(v)))
    this.renderOptions()
    this.updateTrigger()
    
    // Cerrar al hacer click fuera
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
    
    // Si este dropdown estaba abierto antes del turbo update, reabrirlo
    const openDropdownName = window._openDropdownFilter
    if (openDropdownName && openDropdownName === this.nameValue) {
      this.menuTarget.classList.remove("hidden")
      window._openDropdownFilter = null
    }
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  handleOutsideClick(event) {
    // No cerrar si hay un dropdown marcado para reabrir (durante turbo submit)
    if (window._openDropdownFilter) return
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.menuTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    // Cerrar otros dropdowns antes de abrir este
    this.closeOtherDropdowns()
    this.menuTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  closeOtherDropdowns() {
    // Buscar todos los controladores dropdown-filter y cerrar sus menus
    document.querySelectorAll('[data-controller~="dropdown-filter"]').forEach(el => {
      if (el !== this.element) {
        const menu = el.querySelector('[data-dropdown-filter-target="menu"]')
        if (menu && !menu.classList.contains('hidden')) {
          menu.classList.add('hidden')
        }
      }
    })
  }

  close() {
    this.menuTarget.classList.add("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.filter()
    }
  }

  filter() {
    const query = this.hasSearchTarget ? this.searchTarget.value.toLowerCase() : ""
    const items = this.optionsTarget.querySelectorAll("[data-option]")
    
    items.forEach(item => {
      const text = item.dataset.option.toLowerCase()
      if (text.includes(query)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    })
  }

  select(event) {
    const checkbox = event.target
    const value = checkbox.value
    
    if (checkbox.checked) {
      this.selectedValues.add(value)
    } else {
      this.selectedValues.delete(value)
    }
    
    this.updateTrigger()
    this.updateHiddenInputs()
    this.submitForm()  // Auto-submit al seleccionar
  }

  selectAll(event) {
    event.preventDefault()
    const visibleItems = this.optionsTarget.querySelectorAll("[data-option]:not([style*='display: none']) input[type='checkbox']")
    visibleItems.forEach(checkbox => {
      checkbox.checked = true
      this.selectedValues.add(checkbox.value)
    })
    this.updateTrigger()
    this.updateHiddenInputs()
    this.submitForm()  // Auto-submit
  }

  clearAll(event) {
    event.preventDefault()
    this.selectedValues.clear()
    this.optionsTarget.querySelectorAll("input[type='checkbox']").forEach(checkbox => {
      checkbox.checked = false
    })
    this.updateTrigger()
    this.updateHiddenInputs()
    this.submitForm()  // Auto-submit
  }

  updateTrigger() {
    const count = this.selectedValues.size
    const isActive = count > 0
    
    // Actualizar badge
    if (this.hasBadgeTarget) {
      this.badgeTarget.textContent = count
      if (isActive) {
        this.badgeTarget.classList.remove("hidden")
        this.badgeTarget.classList.remove('hidden');
      } else {
        this.badgeTarget.classList.add("hidden")
        this.badgeTarget.classList.add('hidden');
      }
    }
    
    // Actualizar estilos del trigger button (estilo pill activo/inactivo)
    if (this.hasTriggerTarget) {
      const trigger = this.triggerTarget
      const arrow = trigger.querySelector("svg")
      
      if (isActive) {
        // Estado activo: fondo azul claro, texto azul
        trigger.classList.remove("text-gray-700", "hover:bg-gray-50")
        trigger.classList.add("bg-blue-50", "text-blue-800")
        if (arrow) {
          arrow.classList.remove("text-gray-400")
          arrow.classList.add("text-blue-600")
        }
      } else {
        // Estado inactivo: texto gris, hover gris
        trigger.classList.remove("bg-blue-50", "text-blue-800")
        trigger.classList.add("text-gray-700", "hover:bg-gray-50")
        if (arrow) {
          arrow.classList.remove("text-blue-600")
          arrow.classList.add("text-gray-400")
        }
      }
    }
  }

  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return
    
    // Limpiar inputs anteriores
    this.hiddenInputsTarget.innerHTML = ""
    
    // Crear nuevos inputs para cada valor seleccionado
    this.selectedValues.forEach(value => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.nameValue
      input.value = value
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  submitForm() {
    const form = this.element.closest("form")
    if (form) {
      // Guardar el nombre del dropdown abierto para reabrirlo después del turbo update
      window._openDropdownFilter = this.nameValue
      
      // Pequeño delay para permitir que los hidden inputs se actualicen
      setTimeout(() => {
        form.requestSubmit()
      }, 10)
    }
  }

  renderOptions() {
    if (!this.hasOptionsTarget) {
      return
    }
    
    const options = this.optionsValue || []
    
    const html = options.map(opt => {
      const optId = String(opt.id)
      const isSelected = this.selectedValues.has(optId)
      return `
      <label data-option="${opt.name}" 
             class="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
        <input type="checkbox" 
               value="${optId}" 
               ${isSelected ? 'checked' : ''}
               data-action="change->dropdown-filter#select"
               class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
        <span class="flex-1 truncate" title="${opt.name}">${opt.name}</span>
        <span class="text-xs text-gray-400">${opt.count}</span>
      </label>
    `}).join("")
    
    this.optionsTarget.innerHTML = html
  }
}
